import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Home, Clock, Star, Trash2, Wrench, Settings, Link2, Bell, 
  Building2, Users, Heart, ChevronsLeft, ChevronsRight, LogOut, Gavel, HomeIcon, Car, Users2,
  ListTodo, Lock
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import easyfinderLogo from "@/assets/easyfinder-logo-google.png";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { FolderTree } from "./FolderTree";
import { useB2BAccess, type B2BModule } from "@/hooks/useB2BAccess";

interface NavItem {
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  showBadge?: boolean;
  view?: string; // For query param based views
  b2bModule?: B2BModule; // For B2B access gating
}

// Drive-style navigation - Unified Home at /documents
const driveNavItems: NavItem[] = [
  { path: "/documents", icon: Home, label: "Mes documents", view: "all" },
  { path: "/documents", icon: Clock, label: "Récents", view: "recent" },
  { path: "/documents", icon: Star, label: "Favoris", view: "starred" },
  { path: "/documents", icon: Trash2, label: "Corbeille", view: "trash" },
];

// Tools - removed Recherche IA (now integrated in /documents)
const toolsNavItems: NavItem[] = [
  { path: "/actions", icon: Bell, label: "Actions" },
  { path: "/demarches", icon: ListTodo, label: "Démarches" },
  { path: "/pdf-tools", icon: Wrench, label: "Outils PDF" },
];

// Personal features
const personalNavItems: NavItem[] = [
  { path: "/administrative-health", icon: Heart, label: "Santé admin" },
  { path: "/family", icon: Users2, label: "Ma famille" },
];

// Pro/B2B features
const proNavItems: NavItem[] = [
  { path: "/fleet", icon: Car, label: "Flotte", b2bModule: "fleet" },
  { path: "/immo", icon: HomeIcon, label: "Immobilier", b2bModule: "properties" },
  { path: "/tenders", icon: Gavel, label: "Appels d'offres", b2bModule: "tenders" },
  { path: "/legal-documents", icon: Building2, label: "Docs légaux" },
  { path: "/subcontractors", icon: Users, label: "Prestataires", b2bModule: "subcontractors" },
  { path: "/share-links", icon: Link2, label: "Partages" },
];

const bottomNavItems: NavItem[] = [
  { path: "/notifications", icon: Bell, label: "Notifications", showBadge: true },
  { path: "/settings", icon: Settings, label: "Réglages" },
];

interface DesktopSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export const DesktopSidebar = ({ collapsed, onToggleCollapse }: DesktopSidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signOut } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const { canAccessModule, isLoading: b2bLoading } = useB2BAccess();

  useEffect(() => {
    const fetchUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { count } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      setUnreadCount(count || 0);
    };

    fetchUserData();

    const channel = supabase
      .channel("nav-notifications")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications" },
        () => fetchUserData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Déconnexion réussie");
    navigate("/auth");
  };

  const isViewActive = (item: NavItem): boolean => {
    if (item.view) {
      const currentView = searchParams.get("view");
      const currentFolder = searchParams.get("folder");
      
      // If there's a folder param, none of the view items are active
      if (currentFolder) return false;
      
      if (item.view === "all") {
        // "Accueil" is active when on /documents with no view or folder param
        return location.pathname === "/documents" && !currentView && !currentFolder;
      }
      return location.pathname === "/documents" && currentView === item.view;
    }
    return location.pathname === item.path;
  };

  const handleNavClick = (item: NavItem) => {
    if (item.view) {
      if (item.view === "all") {
        navigate("/documents");
      } else {
        navigate(`/documents?view=${item.view}`);
      }
    } else {
      navigate(item.path);
    }
  };

  const renderNavItem = (item: NavItem) => {
    const isActive = isViewActive(item);
    const showBadge = item.showBadge && unreadCount > 0;
    const isLocked = item.b2bModule && !b2bLoading && !canAccessModule(item.b2bModule);

    return (
      <Tooltip key={`${item.path}-${item.view || item.label}`} delayDuration={0}>
        <TooltipTrigger asChild>
          <button
            onClick={() => handleNavClick(item)}
            className={cn(
              "group relative flex items-center gap-3 w-full px-3 py-2.5 rounded-lg transition-all duration-200 text-left",
              isActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground hover:scale-[1.02]",
              isLocked && "opacity-60"
            )}
          >
            {/* Active indicator bar */}
            <div
              className={cn(
                "absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-full bg-primary transition-all duration-300",
                isActive ? "h-5 opacity-100" : "h-0 opacity-0"
              )}
            />
            
            <item.icon 
              className={cn(
                "w-5 h-5 shrink-0 transition-transform duration-200",
                !isActive && "group-hover:scale-110"
              )} 
            />
            
            {!collapsed && (
              <span className="text-sm font-medium truncate">{item.label}</span>
            )}
            
            {/* Lock icon for gated modules */}
            {isLocked && !collapsed && (
              <Lock className="w-3.5 h-3.5 ml-auto text-muted-foreground/50" />
            )}
            
            {showBadge && !isLocked && (
              <span
                className={cn(
                  "flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-xs font-medium",
                  collapsed
                    ? "absolute -top-1 -right-1 h-5 min-w-5 px-1"
                    : "ml-auto h-5 min-w-5 px-1.5"
                )}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        </TooltipTrigger>
        {collapsed && (
          <TooltipContent side="right" sideOffset={12}>
            {item.label}
            {isLocked && " 🔒"}
            {showBadge && !isLocked && ` (${unreadCount})`}
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  return (
    <aside
      className={cn(
        "hidden md:flex fixed left-0 top-0 z-40 h-screen flex-col border-r border-border transition-all duration-300 ease-in-out",
        "bg-gradient-to-b from-card to-card/95 backdrop-blur-sm",
        collapsed ? "w-[68px]" : "w-56"
      )}
    >
      {/* Header: Logo */}
      <div className="flex items-center justify-center p-4 border-b border-border/50">
        <div 
          className="flex items-center gap-3 cursor-pointer transition-opacity hover:opacity-80"
          onClick={() => navigate("/documents")}
        >
          <img
            src={easyfinderLogo}
            alt="EasyFinder"
            className={cn(
              "rounded-xl shadow-lg shadow-primary/20 transition-all duration-300",
              collapsed ? "w-9 h-9" : "w-10 h-10"
            )}
          />
          {!collapsed && (
            <span className="text-lg font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              EasyFinder
            </span>
          )}
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {/* Drive-style nav (Accueil, Récents, Favoris, Corbeille) */}
        <div className="space-y-0.5">
          {driveNavItems.map(renderNavItem)}
        </div>

        {/* Separator */}
        <div className="my-4 mx-2 border-t border-border/50" />

        {/* Folder Tree */}
        <FolderTree collapsed={collapsed} />

        {/* Separator */}
        <div className="my-4 mx-2 border-t border-border/50" />

        {/* Tools nav */}
        <div className="space-y-0.5">
          {!collapsed && (
            <p className="text-[11px] text-muted-foreground/70 uppercase tracking-widest px-3 mb-2 font-semibold">
              Outils
            </p>
          )}
          {toolsNavItems.map(renderNavItem)}
        </div>

        {/* Separator */}
        <div className="my-4 mx-2 border-t border-border/50" />

        {/* Personal nav */}
        <div className="space-y-0.5">
          {!collapsed && (
            <p className="text-[11px] text-muted-foreground/70 uppercase tracking-widest px-3 mb-2 font-semibold">
              Personnel
            </p>
          )}
          {personalNavItems.map(renderNavItem)}
        </div>

        {/* Separator */}
        <div className="my-4 mx-2 border-t border-border/50" />

        {/* Pro nav */}
        <div className="space-y-0.5">
          {!collapsed && (
            <p className="text-[11px] text-muted-foreground/70 uppercase tracking-widest px-3 mb-2 font-semibold flex items-center gap-1.5">
              Pro
              <span className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-bold">
                B2B
              </span>
            </p>
          )}
          {proNavItems.map(renderNavItem)}
        </div>
      </nav>

      {/* Bottom Navigation */}
      <div className="px-3 py-3 border-t border-border/50 space-y-1">
        {bottomNavItems.map(renderNavItem)}
        
        {/* Sign out button */}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              onClick={handleSignOut}
              className={cn(
                "group flex items-center gap-3 w-full px-3 py-2.5 rounded-lg transition-all duration-200 text-left",
                "text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              )}
            >
              <LogOut className="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:scale-110" />
              {!collapsed && (
                <span className="text-sm font-medium">Déconnexion</span>
              )}
            </button>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right" sideOffset={12}>
              Déconnexion
            </TooltipContent>
          )}
        </Tooltip>
      </div>

      {/* Collapse toggle - Bottom positioned */}
      <div className="px-3 pb-4 pt-2">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "w-full h-9 text-muted-foreground/70 hover:text-foreground hover:bg-muted/50 transition-all duration-200",
            "border border-border/50 hover:border-border"
          )}
          onClick={onToggleCollapse}
        >
          {collapsed ? (
            <ChevronsRight className="w-4 h-4" />
          ) : (
            <>
              <ChevronsLeft className="w-4 h-4 mr-2" />
              <span className="text-xs">Réduire</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
};

export default DesktopSidebar;
